import {
  call, put, race, select, take, takeEvery,
} from 'redux-saga/effects';
import { getLocation, matchPath, LOCATION_CHANGE } from 'connected-react-router';
import { pick } from 'lodash';
import Api from '../../utils/Api';
import { constants as timeplansConstants } from '../timeplans/redux';
import { actions, constants, selector, EDITABLE_FIELDS } from './redux';

const FORM_ROUTE = { path: '/timeplans/:id/edit' };

// `adapter.apiRequest` resolves `{ data, headers }` where `data` is the
// WHOLE JSON:API document — so the payload sits at `response.data.data`.
//
// The double unwrap is the easiest thing in this file to get wrong, and
// getting it wrong is silent: one unwrap too few yields `undefined`
// spread into an object, the wage block renders every field empty with
// TOTAL 0, and nothing errors anywhere. `owner-details-wage/utils.js`
// destructures `{ data: { data } }` for the same reason.
const jsonApiPayload = (response) => response?.data?.data;

const openSlotId = (pathname) => {
  const match = matchPath(pathname, FORM_ROUTE);
  return match ? match.params.id : null;
};

// CREW-1184 — the wage block is hidden for a slot with no seafarer, so
// there is nothing to fetch (and nothing to create) for a vacancy.
const slotHasSeafarer = (timeplan) => Boolean(timeplan && timeplan.userId);

/**
 * Fetch the slot's wage, creating a prefilled row the first time.
 *
 * Runs off two triggers, both guarded by the route and by `loadedForId`:
 * the form's own mount, and the slot list landing. The second one matters
 * for a deep link to /timeplans/:id/edit, where at mount time the slot is
 * not in the store yet and there is no way to tell a vacancy from a manned
 * slot.
 */
function* loadSlotWage() {
  const { pathname } = yield select(getLocation);
  const timePlanId = openSlotId(pathname);
  if (!timePlanId) return;

  const { loading, loadedForId } = yield select(selector);
  if (loading || loadedForId === timePlanId) return;

  // Not in the store yet — the ITEMS_LOADED_SUCCESSFULLY trigger will
  // bring us back here once it is.
  const timeplan = yield select(
    (s) => (s.timeplans.data || []).find((t) => t.id === timePlanId),
  );
  if (!timeplan) return;

  if (!slotHasSeafarer(timeplan)) {
    yield put(actions.loadSlotWageSuccess({ timePlanId, slotWage: null }));
    return;
  }

  try {
    yield put(actions.loadSlotWageRequest());

    const found = yield call(Api.crewWages.getSlotWage, { timePlanId });
    const rows = jsonApiPayload(found) || [];

    // One row per slot, so an empty collection means "never had a wage".
    // Creating it with nothing but the slot id is what triggers the
    // backend's prefill from the owner's rate card.
    let row = rows[0];
    if (!row) {
      const created = yield call(Api.crewWages.createSlotWage, { time_plan_id: timePlanId });
      row = jsonApiPayload(created);
    }

    yield put(actions.loadSlotWageSuccess({
      timePlanId,
      slotWage: row ? { id: row.id, ...row.attributes } : null,
    }));
  } catch (e) {
    console.error(e);

    yield put(actions.loadSlotWageError(e));
  }
}

function* clearOnLeavingForm({ payload: { location } }) {
  if (openSlotId(location.pathname)) return;

  const { loadedForId } = yield select(selector);
  if (loadedForId) yield put(actions.resetSlotWage());
}

/**
 * Save the wage — but only after the slot itself has saved.
 *
 * The order is the point. The slot carries the validation (travel dates
 * against sign-on/sign-off) and the form's error display; if it is
 * rejected, the coordinator is looking at a form that did not save, and a
 * wage row quietly persisted underneath it would be a figure nobody
 * agreed to. So this waits for the timeplans saga's own outcome and does
 * nothing on failure.
 */
function* saveSlotWage({ payload }) {
  const values = payload && payload.slot_wage;
  if (!values) return;

  const { success } = yield race({
    success: take(timeplansConstants.ITEM_UPDATED_SUCCESSFULLY),
    failure: take(timeplansConstants.UPDATE_ITEM_ERROR),
  });
  if (!success) return;

  const { data: loaded } = yield select(selector);
  // Only the negotiated lines. The six matrix lines are read-only on the
  // API and belong to the owner's rate card — sending them back would be
  // rejected outright (`updatable_fields`), taking the whole save with it.
  const attributes = pick(values, EDITABLE_FIELDS);

  try {
    yield put(actions.saveSlotWageStarted());

    const saved = loaded && loaded.id
      ? yield call(Api.crewWages.updateSlotWage, loaded.id, attributes)
      : yield call(Api.crewWages.createSlotWage, { time_plan_id: payload.id, ...attributes });

    const row = jsonApiPayload(saved);

    yield put(actions.saveSlotWageSuccess(row ? { id: row.id, ...row.attributes } : null));
  } catch (e) {
    console.error(e);

    yield put(actions.saveSlotWageError(e));
  }
}

/**
 * CREW-1101 — pull this slot's wage back onto the owner's current rate card.
 *
 * Unlike `saveSlotWage` above this does NOT wait on the slot's own save: it
 * writes nothing the form holds, so there is no order to respect and nothing
 * to leave half-applied. Reuses `saveSlotWageSuccess` because the outcome is
 * the same — a fresh row in the slice, which `SlotWageFields` re-hydrates from.
 */
function* resyncSlotWage() {
  const { data: loaded } = yield select(selector);
  if (!loaded || !loaded.id) return;

  try {
    yield put(actions.saveSlotWageStarted());

    const response = yield call(Api.crewWages.resyncSlotWage, loaded.id);
    const row = jsonApiPayload(response);

    yield put(actions.saveSlotWageSuccess(row ? { id: row.id, ...row.attributes } : null));
  } catch (e) {
    console.error(e);

    yield put(actions.saveSlotWageError(e));
  }
}

export default function getFeatureSagas() {
  return [
    takeEvery(
      [timeplansConstants.LOAD_ITEM_REQUEST, timeplansConstants.ITEMS_LOADED_SUCCESSFULLY],
      loadSlotWage,
    ),
    takeEvery(LOCATION_CHANGE, clearOnLeavingForm),
    takeEvery(timeplansConstants.UPDATE_ITEM_REQUEST, saveSlotWage),
    takeEvery(constants.RESYNC_SLOT_WAGE_REQUEST, resyncSlotWage),
  ];
}

export { loadSlotWage, saveSlotWage, resyncSlotWage, clearOnLeavingForm, constants };
